/**
 * VOLLEYBALL DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated volleyball drills organized into skill levels:
 * 1. Beginner Passing (30 minutes) - Basic bumping and ball control
 * 2. Intermediate Passing (45 minutes) - Advanced ball control and movement
 * 3. Advanced Passing (45 minutes) - Complex passing sequences
 * 4. Beginner Setting (30 minutes) - Basic setting technique
 * 5. Intermediate Setting (45 minutes) - Advanced setting skills
 * 6. Advanced Setting (45 minutes) - Complex setting combinations
 * 7. Beginner Serving (30 minutes) - Basic serving form
 * 8. Intermediate Serving (45 minutes) - Advanced serving techniques
 * 9. Advanced Serving (45 minutes) - Complex serving patterns
 * 10. Complete Individual Volleyball (60 minutes) - Comprehensive volleyball workout
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 9001 (individual volleyball drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'volleyball'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Passing', 'Setting', 'Serving')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'Volleyball, wall space')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * VOLLEYBALL DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (wall work, self-practice)
 * - Equipment limited to volleyball and wall space
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental volleyball techniques and coordination
 * - Progressive skill development within each drill
 * - Emphasis on proper form and technique
 *
 * CREATED: [Date] - Volleyball drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const volleyballDrills = [
  // BEGINNER PASSING (30 minutes) Done
  {
    id: 2051,
    name: 'Beginner Passing',
    categories: ['Passing', 'beginner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Passing',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to eight feet from the wall or kneel if space is tight. Chest height passes only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, straight arms, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Platform taps. Tap the ball gently up. Arms stay straight.',
      },
      {
        time: 72,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Reminder. Two tracks, knees over toes, tall spine.' },
      {
        time: 120,
        text: 'Five soft self passes. Light touch to chest height, then catch.',
      },
      { time: 132, text: 'Reset posture. Head over hips. Feet quiet.' },
      { time: 144, text: 'Aim chest height on the wall. Eyes on the target.' },
      { time: 156, text: 'Thumbs together on the platform. Wrists stay flat.' },
      {
        time: 168,
        text: 'Shoulders forward. Light weight on the balls of the feet.',
      },
      { time: 192, text: 'Last warm up cue. Smooth lift from the legs.' },
      { time: 216, text: 'Get ready for wall passes.' },

      // Stance 1: Stationary wall pass 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Pass. Platform set to the middle. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Pass to the wall at chest height. Catch the rebound.',
      },
      {
        time: 276,
        text: 'Lift from your legs. Arms stay straight. Breathe out on the pass.',
      },
      { time: 294, text: 'Aim small. Hit the tape box on the wall.' },
      { time: 312, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 330, text: 'Five clean passes in a row. Count together.' },
      {
        time: 348,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      {
        time: 366,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 384, text: 'Make it eight clean passes to the box.' },
      { time: 402, text: 'Breathe steady. Tall spine.' },
      { time: 420, text: 'Last two passes here. Soft touch.' },
      { time: 438, text: 'Transition. Quick shake out.' },

      // Stance 2: Kneeling wall pass 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Wall Pass. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Pass to the wall at chest height. Catch the rebound.',
      },
      { time: 576, text: 'Platform still. Shoulders guide. Arms straight.' },
      { time: 594, text: 'Breathe out on the pass. Quiet knees.' },
      { time: 612, text: 'Angle check. Point your platform to the box.' },
      { time: 630, text: 'Five clean kneeling passes. Count together.' },
      {
        time: 648,
        text: 'Pulse. Three pretend passes with no ball. Feel the path.',
      },
      { time: 666, text: 'Make it eight clean kneeling passes.' },
      {
        time: 684,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 702, text: 'Last round kneeling. Smooth pass, soft catch.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: One knee angle wall pass 840–1140
      {
        time: 840,
        text: 'Stance 3. One Knee Angle Pass. Passing side knee down, other foot forward on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 858,
        text: 'Pass slightly left. Angle platform left. Hit the box.',
      },
      {
        time: 876,
        text: 'Pass slightly right. Angle platform right. Hit the box.',
      },
      {
        time: 894,
        text: 'Front toe points to the wall. Small shift if needed.',
      },
      { time: 912, text: 'Three left and three right. Count together.' },
      {
        time: 930,
        text: 'Cues. Thumbs together, wrists flat, lift from legs.',
      },
      { time: 948, text: 'Pulse. Hold your angle for 3, 2, 1, then pass.' },
      { time: 966, text: 'Breathe steady. Head over hips.' },
      { time: 984, text: 'Last round on one knee. Smooth angle, soft pass.' },
      { time: 1002, text: 'Transition. Stand up. Shake out arms.' },

      // Stance 4: Micro shuffle wall pass 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Wall Pass. One small shuffle to line up, then pass. Hold for 3, 2, 1.',
      },
      {
        time: 1158,
        text: 'Shuffle left, stop, platform set, pass to the box.',
      },
      {
        time: 1176,
        text: 'Shuffle right, stop, platform set, pass to the box.',
      },
      { time: 1194, text: 'Switch sides after four passes. Keep the rhythm.' },
      {
        time: 1212,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1230,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1248, text: 'Count six each side with smooth stops.' },
      { time: 1266, text: 'Aim small. Hit the box.' },
      { time: 1284, text: 'Breathe out on the pass. Tall spine.' },
      { time: 1302, text: 'Last round here. Small steps, strong platform.' },

      // Stance 5: Receive, set feet, pass, reset 1440–1620 (dense real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Receive, Set Feet, Pass, Reset. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1448, text: 'Bounce to self, set your feet, pass to the box.' },
      { time: 1456, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1464, text: 'Beat. Ready, pass, reset. Keep it smooth.' },
      { time: 1472, text: 'Platform strong. Thumbs together, wrists flat.' },
      { time: 1480, text: 'Aim small. Sticker spot in the box.' },
      { time: 1488, text: 'Feet quiet. Toes and knees line up. Tall spine.' },
      { time: 1496, text: 'Count ten clean passes in a row.' },
      { time: 1504, text: 'Breathe out on contact. Head over hips.' },
      { time: 1512, text: 'Micro shuffle if needed, then pass.' },
      {
        time: 1520,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1528, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1536, text: 'Last round. Small steps, soft touch.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling wall pass, ready wall pass, micro shuffle wall pass. Go.',
      },
      { time: 1638, text: 'Repeat the flow. Kneeling, ready, shuffle.' },
      { time: 1656, text: 'Clean six flow reps together. Smooth and quiet.' },
      {
        time: 1674,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1692,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 1710, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1728,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1.',
      },
      {
        time: 1746,
        text: 'Team check. Quiet high fives. Fantastic work today!',
      },
    ],
    timeTotal: 1750,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height box on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // INTERMEDIATE PASSING (45 minutes) Done
  {
    id: 2052,
    name: 'Intermediate Passing',
    categories: ['Passing', 'intermediate'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Passing',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to ten feet from the wall or kneel if space is tight. Chest height passes only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, light weight on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, arms straight, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Platform taps. Tap the ball softly up, then catch. Arms stay straight.',
      },
      {
        time: 72,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks reminder. Knees over toes. Tall spine.' },
      { time: 120, text: 'Five soft self passes to chest height, then catch.' },
      { time: 144, text: 'Reset posture. Head over hips. Feet quiet.' },
      { time: 168, text: 'Aim chest height on the wall. Pick a tape box.' },
      {
        time: 192,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      { time: 216, text: 'Get ready for wall passes.' },

      // Stance 1: Stationary wall target passing 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Target. Platform to the middle. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Pass to the wall at chest height. Catch the rebound.',
      },
      {
        time: 276,
        text: 'Lift from legs. Arms straight. Breathe out on pass.',
      },
      { time: 294, text: 'Aim small. Hit the tape box.' },
      { time: 312, text: 'Feet quiet. Toes and knees line up.' },
      { time: 330, text: 'Count five clean passes. Then reset.' },
      { time: 348, text: 'Pulse. Three tiny knee bends, then pass one.' },
      {
        time: 366,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 384, text: 'Make it eight clean passes to the box.' },
      { time: 402, text: 'Breathe steady. Shoulders relaxed.' },
      {
        time: 420,
        text: 'Beat cue. Set, pass, catch, reset. One, two, three.',
      },
      { time: 450, text: 'Count ten in a row. Calm rhythm.' },
      { time: 480, text: 'Last round here. Small angle, strong platform.' },
      { time: 510, text: 'Transition. Shake out arms. Quiet feet.' },

      // Stance 2: Micro shuffle wall pass left and right 600–960
      {
        time: 600,
        text: 'Stance 2. Micro Shuffle Wall Pass. One small shuffle, stop, pass. Hold for 3, 2, 1.',
      },
      { time: 618, text: 'Shuffle left, stop, platform set, pass to the box.' },
      {
        time: 636,
        text: 'Shuffle right, stop, platform set, pass to the box.',
      },
      {
        time: 654,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 672,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 690, text: 'Switch sides after four passes. Keep the rhythm.' },
      { time: 708, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 726, text: 'Aim small. Hit the box each side.' },
      { time: 744, text: 'Count six each side. Smooth stops.' },
      { time: 762, text: 'Breathe out on contact. Head over hips.' },
      { time: 780, text: 'Last round here. Small steps, strong platform.' },
      { time: 798, text: 'Transition. Reset space.' },

      // Stance 3: Forward and back adjust to wall 960–1320
      {
        time: 960,
        text: 'Stance 3. Forward and Back Adjust. Meet short rebounds, tiny retreat for deeper rebounds. Hold for 3, 2, 1.',
      },
      { time: 978, text: 'Short rebound. Step in, platform set, pass to box.' },
      {
        time: 996,
        text: 'Deep rebound. One small step back, platform set, pass to box.',
      },
      {
        time: 1014,
        text: 'Beat cue. Step, set, pass, reset. One, two, three, four.',
      },
      { time: 1032, text: 'Toes and knees line up. Tall spine.' },
      { time: 1050, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      { time: 1068, text: 'Count six short and six deep. Calm rhythm.' },
      {
        time: 1086,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 1104, text: 'Aim small. Box every time.' },
      { time: 1122, text: 'Breathe steady. Quiet heels.' },
      { time: 1140, text: 'Last round here. Smooth adjust, soft pass.' },

      // Stance 4: Micro drop step angle redirect 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Micro Drop Step and Angle. Tiny drop step to center the ball, then angle pass. Hold for 3, 2, 1.',
      },
      {
        time: 1338,
        text: 'Off center target. Drop step, set on two tracks, pass to middle box.',
      },
      {
        time: 1356,
        text: 'Same toss line. Change platform angle to send left box.',
      },
      {
        time: 1374,
        text: 'Same toss line. Change platform angle to send right box.',
      },
      { time: 1392, text: 'Front toe points to the wall. Knees over toes.' },
      { time: 1410, text: 'Lift from legs. Quiet heels. Breathe out on pass.' },
      {
        time: 1428,
        text: 'Count two full patterns. Middle, left, right, middle.',
      },
      { time: 1446, text: 'Pulse. Hold your angle for 3, 2, 1, then pass.' },
      { time: 1464, text: 'Aim small. Corners of the box.' },
      { time: 1482, text: 'Last round here. Smooth drop, strong platform.' },
      { time: 1500, text: 'Transition. Reset posture. Hands ready.' },

      // Stance 5: On the move redirect and recover 1680–2040
      {
        time: 1680,
        text: 'Stance 5. On the Move Redirect. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1692, text: 'Pass to middle box. Tiny recovery step. Reset.' },
      {
        time: 1704,
        text: 'Beat. Move, set, pass, reset. One, two, three, four.',
      },
      { time: 1716, text: 'Platform strong. Thumbs together, wrists flat.' },
      { time: 1728, text: 'Angle left. Same rebound, send to left box.' },
      { time: 1740, text: 'Angle right. Same rebound, send to right box.' },
      { time: 1752, text: 'Light on the balls of the feet. Quiet landings.' },
      { time: 1764, text: 'Count nine moving redirects. Calm rhythm.' },
      { time: 1776, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1788, text: 'Aim small. Sticker spot in the box.' },
      { time: 1800, text: 'Last round here. Small steps, soft touch.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Short step in pass, shuffle left pass, shuffle right pass. Go.',
      },
      {
        time: 2060,
        text: 'Switch roles with yourself. Repeat the three pass pattern.',
      },
      {
        time: 2080,
        text: 'Score one point for box hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between reps. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet heels.' },
      { time: 2140, text: 'Aim small. Sticker spot every pass.' },
      { time: 2160, text: 'Finish with six perfect passes.' },
      { time: 2180, text: 'Reset space. Quick sip if needed.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten passes on a steady beat. Ready, pass, reset. One, two.',
      },
      {
        time: 2360,
        text: 'Keep platform strong. Thumbs together, wrists flat.',
      },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you pass.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relaxed.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm perfect passes.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two passes. Soft touch.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Good session! See you next time!',
      },
    ],
    timeTotal: 2680,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height boxes on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // ADVANCED PASSING (45 minutes) Done
  {
    id: 2053,
    name: 'Advanced Passing',
    categories: ['Passing', 'advanced'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Passing',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to ten feet from the wall or kneel if space is tight. Chest height passes only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, light weight on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',

    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, arms straight, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Platform taps. Tap the ball softly up, then catch. Arms stay straight.',
      },
      {
        time: 72,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks reminder. Knees over toes. Tall spine.' },
      { time: 120, text: 'Five soft self passes to chest height, then catch.' },
      { time: 144, text: 'Reset posture. Head over hips. Feet quiet.' },
      { time: 168, text: 'Pick a tape box on the wall. Aim small.' },
      {
        time: 192,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      { time: 216, text: 'Get ready for wall precision.' },

      // Stance 1: Stationary wall precision 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Precision. Platform to the middle. Hold for 3, 2, 1.',
      },
      {
        time: 252,
        text: 'Pass to the wall at chest height. Catch the rebound.',
      },
      {
        time: 264,
        text: 'Lift from legs. Arms straight. Breathe out on pass.',
      },
      { time: 276, text: 'Aim small. Hit the box ten times. Count together.' },
      { time: 288, text: 'Feet quiet. Toes and knees line up.' },
      {
        time: 300,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 312, text: 'Beat. Set, pass, catch, reset. One, two, three.' },
      {
        time: 324,
        text: 'Add target change. Left side of the box, then right side.',
      },
      { time: 336, text: 'Shoulders forward. Eyes on the ball.' },
      {
        time: 348,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      { time: 360, text: 'Make eight clean to the box edges.' },
      { time: 372, text: 'Tall spine. Quiet heels.' },
      { time: 384, text: 'Last round here. Small angle, strong platform.' },
      { time: 396, text: 'Breathe steady. Calm shoulders.' },
      { time: 408, text: 'Two perfect passes to finish. Hold for 3, 2, 1.' },

      // Stance 2: Micro shuffle wall pass ladder 600–960
      {
        time: 600,
        text: 'Stance 2. Micro Shuffle Ladder. One small shuffle, stop, pass. Hold for 3, 2, 1.',
      },
      {
        time: 612,
        text: 'Shuffle left, stop, pass to left box. Catch and reset.',
      },
      {
        time: 624,
        text: 'Shuffle right, stop, pass to right box. Catch and reset.',
      },
      { time: 636, text: 'Now middle box. Smooth rhythm.' },
      {
        time: 648,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 660,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 672, text: 'Pattern. Left, right, middle. Count one ladder.' },
      { time: 684, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 696, text: 'Aim small. Corners of each box.' },
      { time: 708, text: 'Do three ladders clean. Calm rhythm.' },
      { time: 720, text: 'Reset posture. Head over hips.' },
      { time: 732, text: 'Last round. Small steps, strong platform.' },

      // Stance 3: Forward and back adjust to wall 960–1320
      {
        time: 960,
        text: 'Stance 3. Forward and Back Adjust. Meet short rebounds, tiny retreat for deeper rebounds. Hold for 3, 2, 1.',
      },
      { time: 972, text: 'Short rebound. Step in, platform set, pass to box.' },
      {
        time: 984,
        text: 'Deep rebound. One small step back, platform set, pass to box.',
      },
      {
        time: 996,
        text: 'Beat. Step, set, pass, reset. One, two, three, four.',
      },
      { time: 1008, text: 'Toes and knees line up. Tall spine.' },
      { time: 1020, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      { time: 1032, text: 'Count six short and six deep. Calm rhythm.' },
      {
        time: 1044,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 1056, text: 'Aim small. Box every time.' },
      { time: 1068, text: 'Breathe steady. Quiet heels.' },
      { time: 1080, text: 'Last round. Smooth adjust, soft pass.' },

      // Stance 4: Micro drop step angle redirect 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Micro Drop Step and Angle. Tiny drop step to center the ball, then angle pass. Hold for 3, 2, 1.',
      },
      { time: 1332, text: 'Middle box. Drop step, set on two tracks, pass.' },
      {
        time: 1344,
        text: 'Same line, angle left to left box. Change platform angle.',
      },
      { time: 1356, text: 'Same line, angle right to right box.' },
      { time: 1368, text: 'Front toe points to the wall. Knees over toes.' },
      { time: 1380, text: 'Lift from legs. Quiet heels. Breathe out on pass.' },
      { time: 1392, text: 'Pattern. Middle, left, right, middle. Count one.' },
      { time: 1404, text: 'Pulse. Hold your angle for 3, 2, 1, then pass.' },
      { time: 1416, text: 'Aim small. Box edges clean.' },
      { time: 1428, text: 'Do two clean patterns in a row.' },
      { time: 1440, text: 'Last round here. Smooth drop, strong platform.' },

      // Stance 5: On the move redirect and recover 1680–2040 (dense real-time cues)
      {
        time: 1680,
        text: 'Stance 5. On the Move Redirect. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1688, text: 'Middle box. Pass, tiny recovery step, reset.' },
      {
        time: 1696,
        text: 'Beat. Move, set, pass, reset. One, two, three, four.',
      },
      { time: 1704, text: 'Platform strong. Thumbs together, wrists flat.' },
      { time: 1712, text: 'Angle left. Same rebound, send to left box.' },
      { time: 1720, text: 'Angle right. Same rebound, send to right box.' },
      { time: 1728, text: 'Light on the balls of the feet. Quiet landings.' },
      {
        time: 1736,
        text: 'Call the target. Say middle, left, or right before you pass.',
      },
      { time: 1744, text: 'Breathe out on contact. Tall spine.' },
      { time: 1752, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1760, text: 'Count nine moving redirects. Calm rhythm.' },
      { time: 1768, text: 'Last round here. Small steps, soft touch.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Step in pass, shuffle left pass, shuffle right pass. Go.',
      },
      { time: 2060, text: 'Repeat the same three pass pattern.' },
      {
        time: 2080,
        text: 'Score one point for box hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between reps. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet heels.' },
      { time: 2140, text: 'Aim small. Sticker spot every pass.' },
      { time: 2160, text: 'Finish with six perfect passes.' },
      { time: 2180, text: 'Reset space. Quick sip if needed.' },

      // Speed Circuit 2 2340–2580 (no long silent gaps)
      {
        time: 2340,
        text: 'Speed circuit. Ten passes on a steady beat. Ready, pass, reset. One, two.',
      },
      {
        time: 2360,
        text: 'Keep platform strong. Thumbs together, wrists flat.',
      },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you pass.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relaxed.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm perfect passes.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two passes. Soft touch.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Good session! See you next time!',
      },
    ],
    timeTotal: 2700,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height boxes on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // BEGINNER SETTING (30 minutes) Done
  {
    id: 2054,
    name: 'Beginner Setting',
    categories: ['Setting', 'beginner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Setting',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to eight feet from the wall or kneel if space is tight. Sets aim to a taped box at forehead height. Keep two tracks, knees over toes, tall spine, diamond window, finger pads, elbows out, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand shape. Thumbs and index make a diamond window at your forehead.',
      },
      { time: 24, text: 'Finger pads ready. Elbows out.' },
      { time: 36, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 48, text: 'Wrist circles, ten each way.' },
      {
        time: 60,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 72,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      {
        time: 84,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      { time: 96, text: 'Two tracks reminder. Knees over toes, tall spine.' },
      { time: 108, text: 'Five soft self sets to the window, then catch.' },
      { time: 120, text: 'Breathe out on the set. Shoulders relaxed.' },
      { time: 144, text: 'Aim at the wall target spot at forehead height.' },
      {
        time: 168,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      {
        time: 192,
        text: 'Last warm up cue. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 216, text: 'Get ready for wall sets.' },

      // Stance 1: Stationary wall set 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Set. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Set to the wall target spot, then catch the rebound.',
      },
      { time: 276, text: 'Lift from your legs. Elbows extend, then relax.' },
      { time: 294, text: 'Breathe out on contact. Soft hands.' },
      { time: 312, text: 'Aim small. Hit the box five times. Count together.' },
      {
        time: 330,
        text: 'Reset shape. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 348, text: 'Pulse. Three tiny knee bends, then set one.' },
      { time: 366, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 384, text: 'Make it eight clean wall sets. Calm rhythm.' },
      { time: 402, text: 'Last two sets here. Smooth push, soft catch.' },
      { time: 420, text: 'Breathe steady. Tall spine.' },
      { time: 438, text: 'Transition. Quick shake out.' },

      // Stance 2: Kneeling wall set 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Wall Set. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Hands high. Set to the wall target spot, then catch.',
      },
      { time: 576, text: 'Finger pads do the work. No slap.' },
      { time: 594, text: 'Breathe out on the set. Quiet knees.' },
      { time: 612, text: 'Aim small. Box every time.' },
      {
        time: 630,
        text: 'Pulse. Three pretend sets with no ball. Feel the path.',
      },
      { time: 648, text: 'Count eight clean kneeling sets.' },
      { time: 666, text: 'Reset shape. Diamond window. Hold for 3, 2, 1.' },
      { time: 684, text: 'Last round kneeling. Smooth set, soft hands.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: One knee angle wall set 840–1140
      {
        time: 840,
        text: 'Stance 3. One Knee Angle Set. Setting side knee down, other foot forward. Hold for 3, 2, 1.',
      },
      {
        time: 858,
        text: 'Set slightly left of the box. Turn shoulders a little.',
      },
      {
        time: 876,
        text: 'Set slightly right of the box. Turn shoulders a little.',
      },
      {
        time: 894,
        text: 'Front toe points to the wall. Small shift if needed.',
      },
      { time: 912, text: 'Count three left and three right. Calm rhythm.' },
      { time: 930, text: 'Cues. Diamond window, finger pads, lift from legs.' },
      {
        time: 948,
        text: 'Pulse. Hold hands in the window for 3, 2, 1, then set.',
      },
      { time: 966, text: 'Breathe steady. Head over hips.' },
      { time: 984, text: 'Last round on one knee. Soft touch.' },
      { time: 1002, text: 'Transition. Stand up. Shake out arms.' },

      // Stance 4: Micro shuffle wall set left and right 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Set. One small shuffle to line up, then set. Hold for 3, 2, 1.',
      },
      { time: 1158, text: 'Shuffle left, stop, hands high, set to the box.' },
      { time: 1176, text: 'Shuffle right, stop, hands high, set to the box.' },
      { time: 1194, text: 'Switch sides after four sets. Keep the rhythm.' },
      {
        time: 1212,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1230,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1248, text: 'Count six each side. Smooth stops.' },
      { time: 1266, text: 'Aim small. Corners of the box.' },
      { time: 1284, text: 'Breathe out on the set. Tall spine.' },
      { time: 1302, text: 'Last round here. Small steps, soft hands.' },

      // Stance 5: Receive, set, reset 1440–1620 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Receive, Set, Reset. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 1448,
        text: 'Bounce to self, hands high, set to the wall target spot.',
      },
      { time: 1456, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1464, text: 'Beat. Ready, set, reset. Keep it smooth.' },
      { time: 1472, text: 'Diamond window. Finger pads press and release.' },
      { time: 1480, text: 'Aim small. Box center.' },
      { time: 1488, text: 'Feet quiet. Toes and knees line up. Tall spine.' },
      { time: 1496, text: 'Count ten clean sets in a row. Calm rhythm.' },
      { time: 1504, text: 'Breathe out on contact. Head over hips.' },
      { time: 1512, text: 'Micro shuffle if needed, then set.' },
      {
        time: 1520,
        text: 'Weight light on the balls of the feet. Quiet heels.',
      },
      { time: 1528, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1536, text: 'Last round. Soft hands, high hands.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling wall set, ready wall set, micro shuffle wall set. Go.',
      },
      { time: 1638, text: 'Repeat the flow. Kneeling, ready, shuffle.' },
      { time: 1656, text: 'Clean six flow reps. Smooth and quiet.' },
      {
        time: 1674,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1692,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 1710, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1728,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Good session! See you next time!',
      },
    ],
    timeTotal: 1732,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape forehead-height box on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },
  // INTERMEDIATE SETTING (45 minutes) Done
  {
    id: 2055,
    name: 'Intermediate Setting',
    categories: ['Setting', 'intermediate'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Setting',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to ten feet from the wall or kneel if space is tight. Sets aim to a taped box at forehead height. Keep two tracks, knees over toes, tall spine, diamond window, finger pads, elbows out, light weight on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand shape. Thumbs and index make a diamond window at your forehead.',
      },
      { time: 24, text: 'Finger pads ready. Elbows out. Eyes on the target.' },
      { time: 36, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 48, text: 'Wrist circles, ten each way.' },
      {
        time: 60,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 72,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      {
        time: 84,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      { time: 96, text: 'Two tracks reminder. Knees over toes, tall spine.' },
      { time: 108, text: 'Five soft self sets to the window, then catch.' },
      { time: 120, text: 'Breathe out on the set. Shoulders relaxed.' },
      { time: 144, text: 'Aim at the wall target spot at forehead height.' },
      {
        time: 168,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      {
        time: 192,
        text: 'Last warm up cue. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 216, text: 'Get ready for wall sets.' },

      // Stance 1: Stationary wall precision sets 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Precision. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Set to the wall target spot, then catch the rebound.',
      },
      { time: 276, text: 'Lift from your legs. Elbows extend, then relax.' },
      { time: 294, text: 'Breathe out on contact. Soft hands.' },
      {
        time: 312,
        text: 'Aim small. Five clean hits to the box. Count together.',
      },
      {
        time: 330,
        text: 'Reset shape. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 348, text: 'Pulse. Three tiny knee bends, then one smooth set.' },
      { time: 366, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 384, text: 'Make it eight clean wall sets. Calm rhythm.' },
      { time: 402, text: 'Aim corners of the box. Small angle change.' },
      { time: 420, text: 'Count ten in a row. Breathe steady.' },
      { time: 438, text: 'Last two sets here. Smooth push, soft catch.' },
      { time: 456, text: 'Transition. Shake out arms.' },

      // Stance 2: Micro shuffle wall set left and right 600–960
      {
        time: 600,
        text: 'Stance 2. Micro Shuffle Set. One small shuffle to line up, then set. Hold for 3, 2, 1.',
      },
      { time: 618, text: 'Shuffle left, stop, hands high, set to the box.' },
      { time: 636, text: 'Shuffle right, stop, hands high, set to the box.' },
      { time: 654, text: 'Switch sides after four sets. Keep the rhythm.' },
      {
        time: 672,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 690,
        text: 'Weight light on the balls of the feet. Quiet heels.',
      },
      { time: 708, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 726, text: 'Aim small. Box center each side.' },
      { time: 744, text: 'Count six each side. Smooth stops.' },
      { time: 762, text: 'Breathe out on the set. Tall spine.' },
      { time: 780, text: 'Last round here. Small steps, soft hands.' },
      { time: 798, text: 'Transition. Reset posture.' },

      // Stance 3: Forward and back adjust wall sets 960–1320
      {
        time: 960,
        text: 'Stance 3. Forward and Back Adjust. Meet short rebounds, tiny retreat for deeper rebounds. Hold for 3, 2, 1.',
      },
      { time: 978, text: 'Short rebound. Step in, hands high, set to box.' },
      {
        time: 996,
        text: 'Deep rebound. One small step back, hands high, set to box.',
      },
      {
        time: 1014,
        text: 'Beat. Step, set, recover, reset. One, two, three, four.',
      },
      { time: 1032, text: 'Toes and knees line up. Head over hips.' },
      {
        time: 1050,
        text: 'Lift from legs. Wrists calm. Finger pads push soft.',
      },
      { time: 1068, text: 'Count six short and six deep. Calm rhythm.' },
      {
        time: 1086,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 1104, text: 'Aim small. Box every time.' },
      { time: 1122, text: 'Breathe steady. Quiet heels.' },
      { time: 1140, text: 'Last round. Smooth adjust, soft set.' },

      // Stance 4: Micro pivot and angle redirect 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Micro Pivot and Angle. Tiny pivot on the back foot, then set. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Middle box. Pivot, set feet, set to the box.' },
      {
        time: 1356,
        text: 'Same line. Change hand angle to send left of the box.',
      },
      {
        time: 1374,
        text: 'Same line. Change hand angle to send right of the box.',
      },
      { time: 1392, text: 'Front toe points to the wall. Knees over toes.' },
      {
        time: 1410,
        text: 'Lift from legs. Quiet heels. Breathe out on contact.',
      },
      { time: 1428, text: 'Pattern. Middle, left, right, middle. Count one.' },
      {
        time: 1446,
        text: 'Pulse. Hold hands in the window for 3, 2, 1, then set.',
      },
      { time: 1464, text: 'Aim small. Box edges clean.' },
      { time: 1482, text: 'Do two perfect patterns in a row.' },
      { time: 1500, text: 'Last round here. Smooth pivot, soft touch.' },
      { time: 1518, text: 'Transition. Reset posture. Hands high.' },

      // Stance 5: Quick set on the move 1680–2040 (dense real-time cues)
      {
        time: 1680,
        text: 'Stance 5. Quick Set on the Move. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1688, text: 'Hands high in the window. Finger pads ready.' },
      { time: 1696, text: 'Set to the box. Breathe out on contact.' },
      { time: 1704, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1712, text: 'Beat. Ready, set, recover, reset.' },
      { time: 1720, text: 'Thumbs and index make the window. Elbows out.' },
      { time: 1728, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1736, text: 'Call middle. Set to the box.' },
      { time: 1744, text: 'Call left. Small turn, soft set left.' },
      { time: 1752, text: 'Call right. Small turn, soft set right.' },
      { time: 1760, text: 'Head over hips. Tall spine.' },
      { time: 1768, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1776, text: 'Count ten clean quick sets. Calm rhythm.' },
      { time: 1784, text: 'Micro shuffle if needed, then set.' },
      { time: 1792, text: 'Last round. Soft hands, high hands.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Step in set, shuffle left set, shuffle right set. Go.',
      },
      { time: 2060, text: 'Repeat the same three set pattern.' },
      {
        time: 2080,
        text: 'Score one point for box hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between reps. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 2140, text: 'Aim small. Box center each set.' },
      { time: 2160, text: 'Finish with six perfect sets.' },
      { time: 2180, text: 'Reset space. Quick sip if needed.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten sets on a steady beat. Ready, set, reset. One, two.',
      },
      {
        time: 2360,
        text: 'Hands stay high. Diamond window, finger pads push.',
      },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you set.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm perfect sets.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two sets. Soft touch.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great job! See you next time!',
      },
    ],
    timeTotal: 2674,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape forehead-height box on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // ADVANCED SETTING (45 minutes) Done
  {
    id: 2056,
    name: 'Advanced Setting',
    categories: ['Setting', 'advanced'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Setting',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to ten feet from the wall or kneel if space is tight. Sets aim to a taped box at forehead height. Keep two tracks, knees over toes, tall spine, diamond window, finger pads, elbows out, light weight on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand shape. Thumbs and index make a diamond window at your forehead.',
      },
      { time: 24, text: 'Finger pads ready. Elbows out. Eyes on the target.' },
      { time: 36, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 48, text: 'Wrist circles, ten each way.' },
      {
        time: 60,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 72,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      {
        time: 84,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      { time: 96, text: 'Two tracks reminder. Knees over toes, tall spine.' },
      { time: 108, text: 'Five soft self sets to the window, then catch.' },
      { time: 120, text: 'Breathe out on the set. Shoulders relaxed.' },
      { time: 144, text: 'Aim at the wall target spot at forehead height.' },
      {
        time: 168,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      {
        time: 192,
        text: 'Last warm up cue. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 216, text: 'Get ready for wall precision.' },

      // Stance 1: Stationary wall precision with edge targets 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Wall Precision. Hands high in the window. Hold for 3, 2, 1.',
      },
      { time: 258, text: 'Set to box center, then catch the rebound.' },
      {
        time: 276,
        text: 'Lift from your legs. Elbows extend, then relax. Breathe out on contact.',
      },
      {
        time: 294,
        text: 'Aim small. Five clean hits to box center. Count together.',
      },
      {
        time: 312,
        text: 'Edge work. Hit top edge twice, then bottom edge twice.',
      },
      {
        time: 330,
        text: 'Reset shape. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      {
        time: 348,
        text: 'Corners. Left top, right top, left bottom, right bottom.',
      },
      { time: 366, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 384, text: 'Pulse. Three tiny knee bends, then one smooth set.' },
      { time: 402, text: 'Beat. Set, catch, reset. One, two, three.' },
      { time: 420, text: 'Count ten in a row to center. Calm rhythm.' },
      { time: 438, text: 'Last two sets here. Soft hands, high hands.' },

      // Stance 2: Micro shuffle ladder (left, right, middle) 600–960
      {
        time: 600,
        text: 'Stance 2. Micro Shuffle Ladder. One small shuffle, stop, set. Hold for 3, 2, 1.',
      },
      {
        time: 618,
        text: 'Shuffle left, stop, hands high, set to box. Catch and reset.',
      },
      { time: 636, text: 'Shuffle right, stop, hands high, set to box.' },
      { time: 654, text: 'Now middle box. Smooth rhythm.' },
      { time: 672, text: 'Pattern. Left, right, middle. Count one ladder.' },
      {
        time: 690,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 708,
        text: 'Weight light on the balls of the feet. Quiet heels.',
      },
      { time: 726, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 744, text: 'Do three clean ladders. Aim small.' },
      { time: 762, text: 'Breathe out on contact. Head over hips.' },
      { time: 780, text: 'Last round here. Small steps, soft hands.' },

      // Stance 3: Forward and back adjust doubles 960–1320
      {
        time: 960,
        text: 'Stance 3. Forward and Back Adjust Doubles. Meet short, tiny retreat for deep. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Short rebound. Step in, set to box twice in a row. Catch.',
      },
      {
        time: 996,
        text: 'Deep rebound. Step back, set to box twice in a row. Catch.',
      },
      {
        time: 1014,
        text: 'Beat. Step, set, set, reset. One, two, three, four.',
      },
      { time: 1032, text: 'Toes and knees line up. Tall spine.' },
      {
        time: 1050,
        text: 'Lift from legs. Wrists calm. Finger pads push soft.',
      },
      { time: 1068, text: 'Count four short doubles and four deep doubles.' },
      {
        time: 1086,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 1104, text: 'Aim small. Box center every time.' },
      { time: 1122, text: 'Breathe steady. Quiet heels.' },
      { time: 1140, text: 'Last round. Smooth adjust, clean doubles.' },

      // Stance 4: Micro pivot angle redirect patterns 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Micro Pivot and Angle Patterns. Tiny pivot, then set. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Pattern. Middle, left edge, right edge, middle.' },
      { time: 1356, text: 'Front toe points to the wall. Knees over toes.' },
      { time: 1374, text: 'Hands high in the window. Finger pads press.' },
      { time: 1392, text: 'Do two clean patterns. Calm rhythm.' },
      {
        time: 1410,
        text: 'Pulse. Hold hands in the window for 3, 2, 1, then set.',
      },
      { time: 1428, text: 'Corners pattern. Left top, right top, middle.' },
      {
        time: 1446,
        text: 'Corners pattern. Left bottom, right bottom, middle.',
      },
      { time: 1464, text: 'Breathe out on contact. Quiet heels.' },
      { time: 1482, text: 'Last round here. Smooth pivot, soft touch.' },

      // Stance 5: Quick set on the move with calls 1680–2040 (dense cues)
      {
        time: 1680,
        text: 'Stance 5. Quick Set on the Move. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1688, text: 'Hands high. Diamond window. Finger pads ready.' },
      {
        time: 1696,
        text: 'Coach call. Middle. Set to box, tiny recovery step.',
      },
      { time: 1704, text: 'Coach call. Left. Small turn, soft set left edge.' },
      {
        time: 1712,
        text: 'Coach call. Right. Small turn, soft set right edge.',
      },
      { time: 1720, text: 'Beat. Ready, set, recover, reset.' },
      { time: 1728, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1736, text: 'Head over hips. Tall spine.' },
      { time: 1744, text: 'Call and set five in a row. Calm rhythm.' },
      { time: 1752, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1760, text: 'Micro shuffle if needed, then set.' },
      { time: 1768, text: 'Count ten quick sets total. Smooth and accurate.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Step in set, shuffle left set, shuffle right set. Go.',
      },
      { time: 2060, text: 'Repeat the same three set pattern.' },
      {
        time: 2080,
        text: 'Score one point for box hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between reps. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 2140, text: 'Aim small. Box center each set.' },
      { time: 2160, text: 'Finish with six perfect sets.' },
      { time: 2180, text: 'Reset space. Quick sip if needed.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten sets on a steady beat. Ready, set, reset. One, two.',
      },
      {
        time: 2360,
        text: 'Hands stay high. Diamond window, finger pads push.',
      },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you set.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm perfect sets.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two sets. Soft touch.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great job! See you next time!',
      },
    ],
    timeTotal: 2674,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape forehead-height box on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // BEGINNER SERVING (30 minutes) Done
  {
    id: 2057,
    name: 'Beginner Serving',
    categories: ['Serving', 'beginner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Serving',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to ten feet from the wall or kneel if space is tight. Use a foam volleyball. Chest to low chest wall targets only. Keep two tracks, front toe points to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216
      {
        time: 0,
        text: 'Ready Serving Stance. Two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand open like a paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Shadow swings. Open hand to the wall target spot. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Hit the bottom third of the ball. Say flat hand.',
      },
      { time: 84, text: 'Front toe points to the target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks, knees over toes. Hold for 3, 2, 1.' },
      { time: 120, text: 'Three more shadow swings. Smooth and light.' },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      {
        time: 168,
        text: 'Flat hand, firm wrist. Eyes on the wall target spot.',
      },
      { time: 192, text: 'Aim chest height on the wall. Quiet feet.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Stationary underhand taps 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Underhand Tap. Ready stance. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Tap the ball gently to the wall target spot. Catch the rebound.',
      },
      {
        time: 276,
        text: 'Small step with the front foot. Toe points to target.',
      },
      {
        time: 294,
        text: 'Breathe out on contact. Path stays low and straight.',
      },
      { time: 312, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 330, text: 'Count five clean taps. Then reset.' },
      { time: 348, text: 'Pulse. Three shadow swings, then one tap.' },
      { time: 366, text: 'Aim small. Hit the tape box.' },
      { time: 384, text: 'Make it eight clean taps to the box.' },
      { time: 402, text: 'Last two taps here. Smooth and soft.' },
      { time: 420, text: 'Reset stance. Two tracks, front toe to target.' },
      { time: 438, text: 'Transition. Shake out arms.' },

      // Stance 2: Kneeling underhand serve 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Underhand Serve. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Ball on front hand out front. Strike hand swings up from back hip to the wall target spot.',
      },
      {
        time: 576,
        text: 'Aim chest height. Gentle arc to the box. Catch the rebound.',
      },
      { time: 594, text: 'Breathe out on contact. Quiet knees.' },
      { time: 612, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 630, text: 'Count five clean kneeling serves.' },
      { time: 648, text: 'Pulse. Three slow shadow swings with no ball.' },
      { time: 666, text: 'Make it eight clean kneeling serves.' },
      { time: 684, text: 'Reset shape. Front hand steady. Hold for 3, 2, 1.' },
      { time: 702, text: 'Last round kneeling. Smooth swing, soft finish.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: Half step underhand serve 840–1140
      {
        time: 840,
        text: 'Stance 3. Half Step Serve. Step, swing, serve. Hold for 3, 2, 1.',
      },
      {
        time: 858,
        text: 'Front toe points to target. Head over hips. Eyes on the box.',
      },
      {
        time: 876,
        text: 'No toss. Ball stays still on front hand. Swing your open hand to send it.',
      },
      { time: 894, text: 'Breathe and reset between serves. Calm shoulders.' },
      { time: 912, text: 'Firm wrist at contact. Finish to target.' },
      { time: 930, text: 'Count five clean serves.' },
      { time: 948, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 966, text: 'Aim small. Hit the tape box.' },
      { time: 984, text: 'Make it ten total. Smooth and steady.' },
      { time: 1002, text: 'Last two serves here. Quiet feet.' },
      { time: 1020, text: 'Reset posture. Two tracks. Head over hips.' },
      { time: 1040, text: 'Transition. Quick shake out.' },

      // Stance 4: Micro shuffle aim adjust 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      { time: 1158, text: 'Shuffle left one step, stop, serve to the box.' },
      { time: 1176, text: 'Shuffle right one step, stop, serve to the box.' },
      {
        time: 1194,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 1212, text: 'Breathe out on contact. Head stays over hips.' },
      { time: 1230, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1248, text: 'Count six each side. Calm and accurate.' },
      { time: 1266, text: 'Aim small. Hit the box center.' },
      { time: 1284, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 5: Target ladder 1440–1620 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Target Ladder. Serve to chest, then left shoulder, then right shoulder, then chest. Hold for 3, 2, 1.',
      },
      { time: 1448, text: 'Front toe to target. Flat hand, firm wrist.' },
      { time: 1456, text: 'Chest box. Breathe out on contact.' },
      { time: 1464, text: 'Left shoulder box. Smooth swing.' },
      { time: 1472, text: 'Right shoulder box. Quiet feet.' },
      { time: 1480, text: 'Back to chest. Count one pattern.' },
      { time: 1488, text: 'Aim small. Tape box edges if you can.' },
      { time: 1496, text: 'Head over hips. Two tracks. Knees soft.' },
      { time: 1504, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1512, text: 'Start pattern two. Chest, left, right, chest.' },
      { time: 1520, text: 'Breathe and reset between serves.' },
      { time: 1528, text: 'Count two perfect patterns in a row.' },
      { time: 1536, text: 'Last round. Soft and accurate. Quiet feet.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling serve, half step serve, micro shuffle serve. Go.',
      },
      {
        time: 1640,
        text: 'Repeat the flow. Kneeling, step, shuffle. Calm rhythm.',
      },
      { time: 1660, text: 'Clean six flow reps. Smooth and quiet.' },
      {
        time: 1680,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1700,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 1720, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1740,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Fantastic session! See you next time!',
      },
    ],
    timeTotal: 1744,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height box on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },

  // INTERMEDIATE SERVING (45 minutes) Done
  {
    id: 2058,
    name: 'Intermediate Serving',
    categories: ['Serving', 'intermediate'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Serving',
    points: 45,
    instructions:
      "Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand eight to twelve feet from the wall, or six to eight feet if tight. Use a foam volleyball. Chest to low chest wall targets only. Optional: place a small painter's tape target spot at chest height on the wall. Keep two tracks, front toe to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.",
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready Serving Stance. Two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand is a flat paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Shadow swings. Open hand to the wall target spot. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Hit the bottom third of the ball. Say flat hand.',
      },
      { time: 84, text: 'Front toe points to the target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks. Knees over toes. Hold for 3, 2, 1.' },
      {
        time: 120,
        text: 'Three more shadow swings. Finish your hand to the spot.',
      },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      {
        time: 168,
        text: 'Flat hand, firm wrist. Eyes on the wall target spot.',
      },
      { time: 192, text: 'Aim chest height on the wall. Quiet feet.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Stationary underhand precision to wall 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Precision. Ready stance. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Tap serve to the wall target spot. Catch the rebound.',
      },
      { time: 276, text: 'Small step with the front foot. Toe to target.' },
      { time: 294, text: 'Breathe out on contact. Path low and straight.' },
      { time: 312, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 330, text: 'Count five clean serves to the spot.' },
      { time: 348, text: 'Pulse. Three shadow swings, then one serve.' },
      { time: 366, text: 'Aim small. Same target spot again.' },
      { time: 384, text: 'Make it eight clean in a row. Calm rhythm.' },
      { time: 402, text: 'Last two here. Smooth and soft.' },
      { time: 420, text: 'Reset stance. Two tracks. Head over hips.' },
      { time: 438, text: 'Beat. Ready, serve, reset. One, two.' },
      { time: 456, text: 'Finish your hand to the target. Quiet heels.' },
      { time: 480, text: 'Final round in this stance. Accurate and calm.' },

      // Stance 2: Half step underhand serve 600–960
      {
        time: 600,
        text: 'Stance 2. Half Step Serve. Step, swing, serve. Hold for 3, 2, 1.',
      },
      {
        time: 618,
        text: 'Front toe to target. Head over hips. Eyes on the spot.',
      },
      {
        time: 636,
        text: 'No toss. Ball stays still on front hand. Swing your open hand to send it.',
      },
      { time: 654, text: 'Breathe and reset between serves. Calm shoulders.' },
      { time: 672, text: 'Firm wrist at contact. Finish to the spot.' },
      { time: 690, text: 'Count six serves. Smooth path.' },
      { time: 708, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 726, text: 'Aim small. Same target spot.' },
      { time: 744, text: 'Make it ten total. Quiet feet.' },
      { time: 762, text: 'Last two serves here. Light and accurate.' },
      { time: 780, text: 'Reset posture. Two tracks. Tall spine.' },

      // Stance 3: Micro shuffle aim adjust 960–1320
      {
        time: 960,
        text: 'Stance 3. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Shuffle left one step, stop, serve to the target spot.',
      },
      {
        time: 996,
        text: 'Shuffle right one step, stop, serve to the target spot.',
      },
      {
        time: 1014,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 1032, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1050, text: 'Switch sides every four serves. Keep rhythm.' },
      { time: 1068, text: 'Breathe out on contact. Head over hips.' },
      { time: 1086, text: 'Aim small. Hit the spot each side.' },
      { time: 1104, text: 'Count six each side. Calm and accurate.' },
      { time: 1122, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 4: Reaction call targets on wall 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Reaction Calls. Partner or coach calls middle, left, or right spot. Serve the call. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Middle spot. Flat hand to chest height.' },
      { time: 1356, text: 'Left spot. Smooth swing, firm wrist.' },
      { time: 1374, text: 'Right spot. Quiet feet.' },
      {
        time: 1392,
        text: 'Beat. Ready, call, serve, reset. One, two, three, four.',
      },
      { time: 1410, text: 'Switch the order. Stay accurate.' },
      { time: 1428, text: 'Front toe to target. Head over hips.' },
      { time: 1446, text: 'Aim small. Hit the exact spot.' },
      {
        time: 1464,
        text: 'Count two perfect patterns. Middle, left, right, middle.',
      },
      { time: 1482, text: 'Last round here. Calm breath.' },

      // Stance 5: Serve, recover, ready 1680–2040 (dense real-time cues)
      {
        time: 1680,
        text: 'Stance 5. Serve, Recover, Ready. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 1688,
        text: 'Ball still on front hand. Flat hand strike to the spot.',
      },
      { time: 1696, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1704, text: 'Beat. Ready, serve, recover, reset.' },
      { time: 1712, text: 'Front toe to target. Knees soft.' },
      { time: 1720, text: 'Aim small. Same spot.' },
      { time: 1728, text: 'Firm wrist. Finish to the spot.' },
      { time: 1736, text: 'Quiet feet. Head over hips.' },
      { time: 1744, text: 'Count ten serves. Calm rhythm.' },
      { time: 1752, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1760, text: 'Micro shuffle if needed, then serve.' },
      { time: 1768, text: 'Last round. Soft and accurate.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three serves in a row. Middle spot, left spot, right spot. Go.',
      },
      { time: 2060, text: 'Repeat the same three target pattern.' },
      {
        time: 2080,
        text: 'Score one point for perfect hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between serves. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth steps, quiet landings.' },
      { time: 2140, text: 'Aim small. Same target spots each serve.' },
      { time: 2160, text: 'Finish with six calm perfect serves.' },
      { time: 2180, text: 'Reset space. Quick sip if needed.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten serves on a steady beat. Ready, serve, reset. One, two.',
      },
      { time: 2360, text: 'Keep the hand flat. Firm wrist.' },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you serve.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm accurate serves.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two serves. Soft finish.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Fantastic session! See you next time!',
      },
    ],
    timeTotal: 2674,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height target spot on wall; towel or mat on wall; floor tape dots",
  },

  // ADVANCED SERVING (45 minutes) Done
  {
    id: 2059,
    name: 'Advanced Serving',
    categories: ['Serving', 'advanced'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Serving',
    points: 45,
    instructions:
      "Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand eight to twelve feet from the wall, or six to eight feet if tight. Use a foam volleyball. Chest to low chest wall targets only. Optional: place a small painter's tape target spot at chest height on the wall. Keep two tracks, front toe to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.",
    timedInstructions: [
      // Warm up 0–216
      {
        time: 0,
        text: 'Ready Serving Stance. Two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand is a flat paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      { time: 48, text: 'Fast feet in place. Quiet and light.' },
      {
        time: 60,
        text: 'Shadow swings. Open hand to the wall target spot. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Hit the bottom third of the ball. Say flat hand.',
      },
      { time: 84, text: 'Front toe points to the target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks, knees over toes. Hold for 3, 2, 1.' },
      {
        time: 120,
        text: 'Three more shadow swings. Finish your hand to the spot.',
      },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      {
        time: 168,
        text: 'Flat hand, firm wrist. Eyes on the wall target spot.',
      },
      { time: 192, text: 'Aim chest height on the wall. Quiet feet.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Stationary underhand precision ladder 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Precision Ladder. Hold for 3, 2, 1.',
      },
      { time: 258, text: 'Serve to middle spot. Catch the rebound. Reset.' },
      { time: 276, text: 'Serve to left spot. Small step. Toe to target.' },
      { time: 294, text: 'Serve to right spot. Quiet feet.' },
      { time: 312, text: 'Pattern. Middle, left, right, middle. Count one.' },
      { time: 330, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 348, text: 'Breathe out on contact. Path low and straight.' },
      { time: 366, text: 'Do two clean patterns in a row.' },
      { time: 384, text: 'Aim small. Hit the exact spot.' },
      { time: 402, text: 'Beat. Ready, serve, reset. One, two.' },
      { time: 420, text: 'Finish your hand to the spot. Quiet heels.' },

      // Stance 2: Half step serve to spots 600–960
      {
        time: 600,
        text: 'Stance 2. Half Step Serve. Step, swing, serve. Hold for 3, 2, 1.',
      },
      {
        time: 618,
        text: 'Front toe to target. Head over hips. Eyes on the spot.',
      },
      {
        time: 636,
        text: 'No toss. Ball stays still on front hand. Swing your open hand to send it.',
      },
      { time: 654, text: 'Middle spot. Firm wrist at contact.' },
      { time: 672, text: 'Left spot. Smooth swing.' },
      { time: 690, text: 'Right spot. Quiet feet.' },
      { time: 708, text: 'Pulse. Two shadow step swings, then serve.' },
      { time: 726, text: 'Count nine serves total. Calm rhythm.' },
      { time: 744, text: 'Breathe and reset between serves.' },
      { time: 762, text: 'Last two serves here. Accurate and light.' },

      // Stance 3: Micro shuffle aim adjust 960–1320
      {
        time: 960,
        text: 'Stance 3. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Shuffle left, stop, serve to left spot. Catch and reset.',
      },
      { time: 996, text: 'Shuffle right, stop, serve to right spot.' },
      { time: 1014, text: 'Now middle spot. Smooth rhythm.' },
      {
        time: 1032,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 1050, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1068, text: 'Count two ladders. Left, right, middle, left.' },
      { time: 1086, text: 'Breathe out on contact. Head over hips.' },
      { time: 1104, text: 'Aim small. Hit the spot each time.' },
      { time: 1122, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 4: Reaction call targets 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Reaction Calls. Coach calls middle, left, or right. Serve the call. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Middle. Flat hand to chest height.' },
      { time: 1356, text: 'Left. Smooth swing, firm wrist.' },
      { time: 1374, text: 'Right. Quiet feet.' },
      {
        time: 1392,
        text: 'Beat. Ready, call, serve, reset. One, two, three, four.',
      },
      { time: 1410, text: 'Switch the order. Stay accurate.' },
      { time: 1428, text: 'Front toe to target. Head over hips.' },
      { time: 1446, text: 'Aim small. Exact spot.' },
      {
        time: 1464,
        text: 'Count two perfect patterns. Middle, left, right, middle.',
      },
      { time: 1482, text: 'Last round here. Calm breath.' },

      // Stance 5: Serve, recover, ready with doubles 1680–2040 (dense cues)
      {
        time: 1680,
        text: 'Stance 5. Serve, Recover, Ready Doubles. Hold for 3, 2, 1.',
      },
      {
        time: 1688,
        text: 'Middle spot. Two quick serves in a row. Tiny recovery step.',
      },
      { time: 1696, text: 'Beat. Ready, serve, serve, reset.' },
      { time: 1704, text: 'Left spot. Two in a row. Finish to target.' },
      { time: 1712, text: 'Right spot. Two in a row. Quiet heels.' },
      { time: 1720, text: 'Front toe to target. Knees soft.' },
      { time: 1728, text: 'Firm wrist. Bottom third contact.' },
      {
        time: 1736,
        text: 'Call the spot before you serve. Middle, left, or right.',
      },
      { time: 1744, text: 'Count ten total serves. Calm rhythm.' },
      { time: 1752, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1760, text: 'Last round. Light and accurate.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three serves in a row. Middle, left, right. Go.',
      },
      { time: 2060, text: 'Repeat the same three target pattern.' },
      {
        time: 2080,
        text: 'Score one point for perfect hits. Aim for eight points.',
      },
      { time: 2100, text: 'Breathe and reset between serves. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth steps, quiet landings.' },
      { time: 2140, text: 'Aim small. Same target spots each serve.' },
      { time: 2160, text: 'Finish with six calm perfect serves.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten serves on a steady beat. Ready, serve, reset. One, two.',
      },
      { time: 2360, text: 'Keep the hand flat. Firm wrist. Finish to spot.' },
      {
        time: 2380,
        text: 'Add call. Say middle, left, or right before you serve.',
      },
      { time: 2400, text: 'Count two perfect rounds. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax.' },
      { time: 2440, text: 'Light feet. Toes and knees line up.' },
      { time: 2460, text: 'Final round. Six calm accurate serves.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two serves. Soft finish.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Nice work.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Fantastic session! See you next time!',
      },
    ],
    timeTotal: 2674,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest-height target spots on wall; towel or mat on wall; floor tape dots",
  },

  // COMPLETE INDIVIDUAL VOLLEYBALL (60 minutes)
  {
    id: 2060,
    name: 'Complete Individual Volleyball',
    categories: ['Complete', 'advanced'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 60,
    instructions:
      "Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, pick a safe wall spot, and move breakables. Put a towel or mat on the wall if needed. Stand six to twelve feet from the wall or kneel if space is tight. Use a foam volleyball. Chest to forehead height only. Optional: place small painter's tape target spots at chest and forehead height on the wall. Keep two tracks, knees over toes, tall spine, light weight on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.",
    timedInstructions: [
      // Warm up 0–240
      {
        time: 0,
        text: 'Ready stance. Two tracks, knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      { time: 12, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 24, text: 'Big arm swings, small range. Ribs down.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      { time: 48, text: 'Fast feet in place. Quiet and light.' },
      {
        time: 60,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 72,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      {
        time: 84,
        text: 'Platform shape. Thumbs together, wrists flat, arms straight.',
      },
      {
        time: 96,
        text: 'Diamond window. Thumbs and index make a window at your forehead.',
      },
      {
        time: 108,
        text: 'Open hand for serving. Flat palm, fingers together, wrist firm.',
      },
      {
        time: 120,
        text: 'Light weight on the balls of the feet. Quiet heels.',
      },
      { time: 132, text: 'Five soft self passes to chest height, then catch.' },
      {
        time: 156,
        text: 'Five soft self sets to the forehead window, then catch.',
      },
      { time: 180, text: 'Three shadow serves to the wall target spot.' },
      { time: 204, text: 'Reset posture. Head over hips. Two tracks.' },
      { time: 228, text: 'Aim at the wall target spots. Calm shoulders.' },

      // PASSING BLOCK 240–1320
      // Stance P1: Stationary wall pass 240–600
      {
        time: 240,
        text: 'Passing. Stationary Wall Pass. Platform set to the middle. Hold for 3, 2, 1.',
      },
      {
        time: 252,
        text: 'Pass to the wall target spot at chest height. Catch the rebound.',
      },
      {
        time: 264,
        text: 'Lift from legs. Arms stay straight. Breathe out on pass.',
      },
      { time: 276, text: 'Aim small. Hit the target spot five times.' },
      { time: 288, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      {
        time: 300,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      { time: 312, text: 'Shoulders forward. Eyes on the ball.' },
      { time: 324, text: 'Count ten clean passes. Calm rhythm.' },
      {
        time: 336,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 360, text: 'Last round here. Small angle, strong platform.' },

      // Stance P2: Micro shuffle wall pass 600–960
      {
        time: 600,
        text: 'Micro Shuffle Wall Pass. One small shuffle, stop, pass. Hold for 3, 2, 1.',
      },
      {
        time: 612,
        text: 'Shuffle left, stop, platform set, pass to the target spot.',
      },
      {
        time: 624,
        text: 'Shuffle right, stop, platform set, pass to the target spot.',
      },
      {
        time: 636,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 648, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 660, text: 'Pattern. Left, right, middle. Count one ladder.' },
      { time: 672, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 684, text: 'Aim small. Hit the spot each side.' },
      { time: 708, text: 'Count three clean ladders. Smooth stops.' },
      { time: 732, text: 'Breathe out on contact. Head over hips.' },

      // Stance P3: Forward and back adjust 960–1260
      {
        time: 960,
        text: 'Forward and Back Adjust. Meet short rebounds, tiny retreat for deep. Hold for 3, 2, 1.',
      },
      {
        time: 972,
        text: 'Short rebound. Step in, platform set, pass to spot.',
      },
      {
        time: 984,
        text: 'Deep rebound. One small step back, platform set, pass to spot.',
      },
      {
        time: 996,
        text: 'Beat. Step, set, pass, reset. One, two, three, four.',
      },
      { time: 1008, text: 'Toes and knees line up. Tall spine.' },
      { time: 1020, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      { time: 1032, text: 'Count six short and six deep. Calm rhythm.' },
      {
        time: 1056,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 1080, text: 'Aim small. Spot every time.' },
      { time: 1104, text: 'Last round here. Smooth adjust, soft pass.' },

      // Passing Accuracy Circuit 1260–1320
      {
        time: 1260,
        text: 'Passing circuit. Three in a row. Short step in pass, shuffle left pass, shuffle right pass. Go.',
      },
      {
        time: 1290,
        text: 'Score one point for perfect hits. Aim for six points. Breathe steady.',
      },

      // SETTING BLOCK 1320–2340
      // Stance S1: Stationary wall set 1320–1680
      {
        time: 1320,
        text: 'Setting. Stationary Wall Set. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 1332,
        text: 'Set to the wall target spot at forehead height. Catch the rebound.',
      },
      { time: 1344, text: 'Finger pads push. Elbows extend, then relax.' },
      { time: 1356, text: 'Lift from legs. Breathe out on the set.' },
      { time: 1368, text: 'Aim small. Five clean sets to the spot.' },
      { time: 1380, text: 'Feet quiet. Two tracks. Knees over toes.' },
      {
        time: 1392,
        text: 'Pulse. Three tiny knee bends, then one smooth set.',
      },
      { time: 1416, text: 'Count ten in a row. Calm rhythm.' },
      {
        time: 1440,
        text: 'Reset window. Thumbs and index make the diamond. Hold for 3, 2, 1.',
      },

      // Stance S2: Micro shuffle wall set 1680–2040
      {
        time: 1680,
        text: 'Micro Shuffle Wall Set. One small shuffle to line up, then set. Hold for 3, 2, 1.',
      },
      {
        time: 1692,
        text: 'Shuffle left, stop, hands high, set to the target spot.',
      },
      {
        time: 1704,
        text: 'Shuffle right, stop, hands high, set to the target spot.',
      },
      {
        time: 1716,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 1728, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 1740, text: 'Pattern. Left, right, middle. Count one ladder.' },
      { time: 1752, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 1776, text: 'Aim small. Spot each time.' },
      { time: 1800, text: 'Count three clean ladders. Smooth stops.' },

      // Stance S3: Forward and back adjust sets 2040–2280
      {
        time: 2040,
        text: 'Forward and Back Adjust Sets. Meet short rebound, tiny retreat for deep. Hold for 3, 2, 1.',
      },
      { time: 2052, text: 'Short. Step in, hands high, set to spot.' },
      {
        time: 2064,
        text: 'Deep. One small step back, hands high, set to spot.',
      },
      {
        time: 2076,
        text: 'Beat. Step, set, recover, reset. One, two, three, four.',
      },
      { time: 2088, text: 'Finger pads. Elbows out. Breathe out on contact.' },
      { time: 2100, text: 'Count six short and six deep. Calm rhythm.' },
      {
        time: 2124,
        text: 'Pulse. Two step in and step back reps with no ball.',
      },
      { time: 2148, text: 'Aim small. Spot every time.' },

      // Setting Accuracy Circuit 2280–2340
      {
        time: 2280,
        text: 'Setting circuit. Three in a row. Step in set, shuffle left set, shuffle right set. Go.',
      },
      {
        time: 2310,
        text: 'Score one point for perfect hits. Aim for six points. Relax shoulders.',
      },

      // SERVING BLOCK 2340–3180
      // Stance V1: Half step underhand serve 2340–2700
      {
        time: 2340,
        text: 'Serving. Half Step Serve. Step, swing, serve. Hold for 3, 2, 1.',
      },
      {
        time: 2352,
        text: 'Front toe to target. Head over hips. Eyes on the spot.',
      },
      {
        time: 2364,
        text: 'No toss. Ball stays still. Swing your flat hand to send it.',
      },
      { time: 2376, text: 'Breathe out on contact. Path low and straight.' },
      { time: 2388, text: 'Firm wrist. Bottom third contact.' },
      { time: 2400, text: 'Count six serves to the middle spot.' },
      { time: 2412, text: 'Pulse. Two shadow step swings, then serve.' },
      { time: 2436, text: 'Aim small. Same spot again.' },
      { time: 2460, text: 'Make it ten total. Quiet feet.' },

      // Stance V2: Micro shuffle aim adjust 2700–3000
      {
        time: 2700,
        text: 'Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      { time: 2712, text: 'Shuffle left, stop, serve to left spot.' },
      { time: 2724, text: 'Shuffle right, stop, serve to right spot.' },
      { time: 2736, text: 'Now middle spot. Smooth rhythm.' },
      {
        time: 2748,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 2760, text: 'Light on the balls of the feet. Quiet heels.' },
      { time: 2772, text: 'Count two ladders. Left, right, middle, left.' },
      { time: 2796, text: 'Breathe and reset between serves. Tall spine.' },

      // Stance V3: Reaction call serve 3000–3120
      {
        time: 3000,
        text: 'Reaction Calls. Coach calls middle, left, or right. Serve the call. Hold for 3, 2, 1.',
      },
      { time: 3012, text: 'Middle. Flat hand to chest height.' },
      { time: 3024, text: 'Left. Smooth swing, firm wrist.' },
      { time: 3036, text: 'Right. Quiet feet.' },
      {
        time: 3048,
        text: 'Beat. Ready, call, serve, reset. One, two, three, four.',
      },
      { time: 3072, text: 'Aim small. Exact spot. Calm breath.' },

      // Serving Accuracy Circuit 3120–3180
      {
        time: 3120,
        text: 'Serving circuit. Three serves. Middle, left, right. Go.',
      },
      {
        time: 3150,
        text: 'Score one point for perfect hits. Aim for six points. Relax shoulders.',
      },

      // COMBO FLOW 3180–3420
      {
        time: 3180,
        text: 'Combo flow. Pass, set, serve. One of each in a row. Hold for 3, 2, 1.',
      },
      { time: 3192, text: 'Pass to wall target spot. Catch.' },
      { time: 3204, text: 'Set to forehead target spot. Catch.' },
      { time: 3216, text: 'Half step serve to chest target spot. Reset.' },
      {
        time: 3228,
        text: 'Beat. Pass, set, serve, reset. One, two, three, four.',
      },
      { time: 3240, text: 'Foot angles. Toes and knees line up. Quiet heels.' },
      { time: 3252, text: 'Count four clean cycles. Calm rhythm.' },
      {
        time: 3276,
        text: 'Pulse before the set. Three tiny knee bends. Hold for 3, 2, 1.',
      },
      { time: 3300, text: 'Aim small. Hit each target spot.' },
      { time: 3324, text: 'Last two cycles. Smooth hands, soft touch.' },

      // COOLDOWN 3420–3600
      {
        time: 3420,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 3450,
        text: 'Forearm and finger stretch. Palms down, then up. Hold for 3, 2, 1.',
      },
      { time: 3480, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 3510,
        text: 'Neck stretch. Look left and right, slow. Hold for 3, 2, 1.',
      },
      {
        time: 3540,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Fantastic session! See you next time!',
      },
    ],
    timeTotal: 3546,
    equipment:
      "Foam volleyball (youth no-sting), wall space; Optional: painter's tape chest and forehead-height target spots on wall; towel or mat on wall; floor tape dots; kneeling pad",
  },
];

export default volleyballDrills;
